import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { formatK } from "../cashflow/CashflowContext";
import type { Currency } from "../../types";
import {
  agingFilterValueFor,
  type AgingBucket,
} from "../../utils/receivablesAgingBuckets";
import { PieChartEmptyContent } from "../datamintEmptyStates";

type AgingTooltipPayload = {
  name: string;
  value: number;
  color: string;
};

type ReceivablesAgingBreakdownCardProps = {
  title?: string;
  agingBuckets: AgingBucket[];
  openBalanceAmount: number;
  displayCurrency: string;
};

export function ReceivablesAgingBreakdownCard({
  title = "Aging breakdown",
  agingBuckets,
  openBalanceAmount,
  displayCurrency,
}: ReceivablesAgingBreakdownCardProps) {
  const navigate = useNavigate();
  // Recharts' Pie-level onClick does not fire for real pointer input here,
  // so the donut tracks which slice is hovered and the wrapper handles the
  // click. You have to be over a slice to click it anyway, so this behaves
  // identically for a user and works with genuine mouse events.
  const [hoveredBucket, setHoveredBucket] = useState<string | null>(null);

  const formatAgingAmount = (amount: number) =>
    formatK(Math.round(amount), displayCurrency as Currency);

  /**
   * Drill into the bucket: open the receivables table pre-filtered to it,
   * which is where the customer names and amounts behind the slice live.
   */
  const drillIntoBucket = (bucketName: string) => {
    const filterValue = agingFilterValueFor(bucketName);
    if (!filterValue) return;
    navigate(`/receivables?aging=${filterValue}&view=customers`);
  };

  const renderAgingPieTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      payload: AgingTooltipPayload;
      value: number;
      name: string;
    }>;
  }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0];
    const amount = Number(point.value) || 0;
    return (
      <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-2.5 py-1.5 text-xs shadow-lg backdrop-blur-sm">
        <p className="font-medium text-[#111827]">{point.name}</p>
        <p className="tabular-nums text-[#374151]">
          {formatAgingAmount(amount)}
        </p>
        <p className="mt-0.5 text-[#898781]">Click to see customers</p>
      </div>
    );
  };

  return (
    <article className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-base font-semibold text-[#111827]">{title}</h3>
      {openBalanceAmount === 0 ? (
        <PieChartEmptyContent ctaTo="/files-integrations" />
      ) : (
        <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:justify-start">
          <div
            className="relative h-52 w-52 shrink-0"
            onClick={() => {
              if (hoveredBucket) drillIntoBucket(hoveredBucket);
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={agingBuckets}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={82}
                  stroke="#ffffff"
                  strokeWidth={2}
                  isAnimationActive={false}
                  onMouseEnter={(entry: { name?: string }) => {
                    setHoveredBucket(entry?.name ?? null);
                  }}
                  onMouseLeave={() => setHoveredBucket(null)}
                >
                  {agingBuckets.map((bucket) => (
                    <Cell key={bucket.name} fill={bucket.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={renderAgingPieTooltip}
                  allowEscapeViewBox={{ x: true, y: true }}
                  wrapperStyle={{ zIndex: 9999, pointerEvents: "none" }}
                  cursor={{ fill: "rgba(15, 23, 42, 0.08)" }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-sm text-[#6B7280]">Total</span>
              <span className="text-2xl font-semibold tabular-nums text-[#111827]">
                {formatAgingAmount(openBalanceAmount)}
              </span>
            </div>
          </div>
          <div className="grid w-fit auto-rows-min grid-cols-[auto_2.75rem_auto] items-center gap-x-5 gap-y-1 py-1">
            {agingBuckets.map((bucket) => {
              const percentage =
                openBalanceAmount > 0
                  ? (bucket.value / openBalanceAmount) * 100
                  : 0;
              const canDrill = agingFilterValueFor(bucket.name) !== null;
              // The legend is the keyboard-reachable twin of the slices —
              // a pie sector can't take focus, so the drill-down lives here
              // too rather than being mouse-only.
              const rowClass = canDrill
                ? "col-span-3 grid cursor-pointer grid-cols-subgrid items-center rounded-md px-2 py-1.5 text-left transition-colors hover:bg-[#F3F4F6] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2a78d6]"
                : "col-span-3 grid grid-cols-subgrid items-center px-2 py-1.5 text-left";
              const content = (
                <Fragment>
                  <span className="flex items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: bucket.color }}
                    />
                    <span className="text-sm text-[#374151]">{bucket.name}</span>
                  </span>
                  <span className="text-right text-sm tabular-nums text-[#9CA3AF]">
                    {Math.round(percentage)}%
                  </span>
                  <span className="text-sm font-medium tabular-nums text-[#111827]">
                    {formatAgingAmount(bucket.value)}
                  </span>
                </Fragment>
              );

              return canDrill ? (
                <button
                  key={bucket.name}
                  type="button"
                  onClick={() => drillIntoBucket(bucket.name)}
                  className={rowClass}
                  title={`Show ${bucket.name} customers`}
                >
                  {content}
                </button>
              ) : (
                <div key={bucket.name} className={rowClass}>
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}
